// Builders that turn structured input into the encoded QR payload string.
// Pure functions — easy to unit test, no QR rendering here.

export type QrType = 'text' | 'vcard' | 'wifi' | 'email' | 'tel' | 'sms';

export type SocialKey = 'linkedin' | 'twitter' | 'instagram' | 'facebook' | 'github' | 'xing';

/** Common social networks offered in the vCard form. `build` turns a bare handle
 *  into a profile URL; a full URL entered by the user is used as-is. */
export const SOCIAL_NETWORKS: { key: SocialKey; label: string; build: (handle: string) => string }[] = [
  { key: 'linkedin', label: 'LinkedIn', build: (h) => `https://www.linkedin.com/in/${h}` },
  { key: 'twitter', label: 'X (Twitter)', build: (h) => `https://x.com/${h}` },
  { key: 'instagram', label: 'Instagram', build: (h) => `https://instagram.com/${h}` },
  { key: 'facebook', label: 'Facebook', build: (h) => `https://facebook.com/${h}` },
  { key: 'github', label: 'GitHub', build: (h) => `https://github.com/${h}` },
  { key: 'xing', label: 'XING', build: (h) => `https://www.xing.com/profile/${h}` },
];

export interface VCardInput {
  firstName: string;
  lastName: string;
  org: string;
  title: string;
  phone: string;
  email: string;
  url: string;
  /** Per-network handle or full URL; empty values are skipped. */
  socials: Partial<Record<SocialKey, string>>;
}

/** Resolves a social value to a full URL (full URL kept as-is, otherwise built from the handle). */
export function socialUrl(network: { build: (h: string) => string }, value: string): string {
  const v = value.trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return network.build(v.replace(/^@/, ''));
}

export interface WifiInput {
  ssid: string;
  password: string;
  encryption: 'WPA' | 'WEP' | 'nopass';
  hidden: boolean;
}

export interface EmailInput {
  to: string;
  subject: string;
  body: string;
}

export interface SmsInput {
  number: string;
  message: string;
}

// Escape characters with special meaning in vCard values.
function vcardEscape(v: string): string {
  return v.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

// Escape characters with special meaning in the WIFI: payload.
function wifiEscape(v: string): string {
  return v.replace(/([\\;,:"])/g, '\\$1');
}

export function buildVCard(v: VCardInput): string {
  const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
  lines.push(`N:${vcardEscape(v.lastName)};${vcardEscape(v.firstName)};;;`);
  const fn = `${v.firstName} ${v.lastName}`.trim();
  if (fn) lines.push(`FN:${vcardEscape(fn)}`);
  if (v.org) lines.push(`ORG:${vcardEscape(v.org)}`);
  if (v.title) lines.push(`TITLE:${vcardEscape(v.title)}`);
  if (v.phone) lines.push(`TEL;TYPE=CELL:${vcardEscape(v.phone)}`);
  if (v.email) lines.push(`EMAIL:${vcardEscape(v.email)}`);
  if (v.url) lines.push(`URL:${vcardEscape(v.url)}`);

  // Social profiles as labeled URLs (Apple itemN.URL / itemN.X-ABLabel convention,
  // widely understood by iOS/macOS and Android contacts apps).
  let item = 1;
  for (const net of SOCIAL_NETWORKS) {
    const url = socialUrl(net, v.socials?.[net.key] ?? '');
    if (url) {
      lines.push(`item${item}.URL:${vcardEscape(url)}`);
      lines.push(`item${item}.X-ABLabel:${vcardEscape(net.label)}`);
      item++;
    }
  }

  lines.push('END:VCARD');
  return lines.join('\n');
}

export function buildWifi(w: WifiInput): string {
  if (!w.ssid) return '';
  const parts = [`T:${w.encryption}`, `S:${wifiEscape(w.ssid)}`];
  if (w.encryption !== 'nopass') parts.push(`P:${wifiEscape(w.password)}`);
  if (w.hidden) parts.push('H:true');
  return `WIFI:${parts.join(';')};;`;
}

export function buildEmail(e: EmailInput): string {
  if (!e.to) return '';
  const params: string[] = [];
  if (e.subject) params.push(`subject=${encodeURIComponent(e.subject)}`);
  if (e.body) params.push(`body=${encodeURIComponent(e.body)}`);
  return `mailto:${e.to}${params.length ? `?${params.join('&')}` : ''}`;
}

export function buildTel(number: string): string {
  return number ? `tel:${number.replace(/\s+/g, '')}` : '';
}

export function buildSms(s: SmsInput): string {
  if (!s.number) return '';
  const num = s.number.replace(/\s+/g, '');
  return s.message ? `SMSTO:${num}:${s.message}` : `SMSTO:${num}`;
}
